<template>
  <div class="h-full relative dark:bg-gray-950/30">
    <div class="h-full flex flex-col">
      <div :class="headerClass">
        <div class="px-4 md:px-5 pt-3">
          <slot name="actions">
            <div class="flex items-center justify-between" :class="{ 'float-right mt-1' : !$slots['actions-left'] }">
              <div v-if="$slots['actions-left']" class="flex space-x-1 mb-2">
                <slot name="actions-left" />
              </div>

              <div class="flex flex-shrink space-x-1 mb-2">
                <slot name="actions-right" />
                <o-tooltip :text="isModePanel ? $t('components.panel.panelLayout.moveToCentre') : $t('components.panel.panelLayout.moveToRight')">
                  <o-button
                    size="slim"
                    class="hidden md:flex h-8"
                    flat
                    :icon="isModePanel ? 'moveLeft' : 'moveRight'"
                    @click="mode"
                  />
                </o-tooltip>
                <o-tooltip :text="$t('general.close')">
                  <o-button
                    size="slim"
                    class="h-8"
                    flat
                    icon="closePanel"
                    @click="close"
                  />
                </o-tooltip>
              </div>
            </div>
          </slot>
        </div>
        <div class="px-4 md:px-5 py-3 md:py-3">
          <slot name="heading">
            <h2 class="text-lg md:text-xl tracking-tight font-bold leading-none/85 mb-1">
              {{ heading }}
            </h2>
            <div v-if="info || $slots.info" class="mt-1">
              <slot name="info">
                <p v-if="info" class="text-sm md:text-base opacity-80">
                  {{ info }}
                </p>
              </slot>
            </div>
          </slot>
        </div>
      </div>
      <div class="flex-auto flex-shrink h-10">
        <div class="h-full">
          <slot />
        </div>
      </div>
      <div v-if="$slots.footer || showFooter" :class="footerClass">
        <slot name="footer">
          <div>
            <div class="flex justify-between align-items">
              <div class="flex items-center space-x-1">
                <OButton outline flat class="mr-2" @click="onCancel()">
                  {{ $t('general.cancel') }}
                </OButton>
              </div>
              <div class="flex items-center space-x-1">
                <OButton
                  :disabled="!unsavedChanges"
                  variant="primary"
                  outline
                  icon="save"
                  class="mr-2"
                  @click="onSave()"
                >
                  {{ $t('general.save') }}
                </OButton>
                <OButton
                  :disabled="!unsavedChanges"
                  variant="primary"
                  icon="save"
                  class="mr-2"
                  @click="onSave(true)"
                >
                  {{ $t('general.saveAndClose') }}
                </OButton>
              </div>
            </div>
          </div>
        </slot>
      </div>
    </div>

    <transition name="fade">
      <div v-if="loading" class="absolute h-full w-full inset-0 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
        <div class="h-full w-full flex items-center justify-center">
          <div class="rounded-xl flex flex-col items-center justify-center">
            <o-loader />
            <p v-if="loadingLabel" class="mt-4 opacity-60 text-sm -mb-2">
              {{ loadingLabel }}
            </p>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, watchEffect } from 'vue'

import {
  OLoader,
} from '@/components/elements'

const panelCtx = inject<any>('panel')

interface Props {
  heading?: string | null
  info?: string | null
  loading?: boolean
  loadingLabel?: string | null
  shouldDismiss?: Function
  didAttemptToDismiss?: Function | null
  willDismiss?: Function
  didDismiss?: Function
  headerClass?: string
  footerClass?: string
  unsavedChanges?: boolean
  showFooter?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  heading: null,
  info: null,
  loading: true,
  loadingLabel: null,
  shouldDismiss: () => true,
  didAttemptToDismiss: null,
  willDismiss: () => true,
  didDismiss: () => true,
  headerClass: 'bg-gray-50 dark:bg-gray-900 border-b dark:border-gray-800',
  footerClass: 'border-t dark:border-gray-800 px-5 py-3 bg-gray-50 relative dark:bg-gray-900',
  unsavedChanges: false,
  showFooter: true,
})

const emit = defineEmits<{
  (e: 'cancel'): void
  (e: 'save', close: boolean): void
}>()

// Register dismiss lifecycle callbacks on the injected context
// so Panel.close() can read them without $refs traversal
watchEffect(() => {
  if (panelCtx) {
    panelCtx.shouldDismiss = props.shouldDismiss || null
    panelCtx.willDismiss = props.willDismiss || null
    panelCtx.didDismiss = props.didDismiss || null
    panelCtx.didAttemptToDismiss = props.didAttemptToDismiss || null
  }
})

const _isPanel = computed(() => {
  return true
})

const isModePanel = computed(() => {
  return panelCtx?.panelMode === 'panel'
})

const isModeModal = computed(() => {
  return panelCtx?.panelMode === 'modal'
})

function close (force = false) {
  panelCtx?.close(force)
}

function mode () {
  panelCtx.mode()
}

function onCancel () {
  emit('cancel')
}

function onSave (closePanel = false) {
  emit('save', closePanel)
}

defineExpose({ close })
</script>
